import { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { sendPasswordResetEmail, sendSignInLinkToEmail } from "firebase/auth";
import { app, auth } from "../../../firebase";
import {
  CheckCircle2,
  MessageCircle,
  ArrowRight,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import {
  SUBJECT_LIST,
  SUBJECTS,
  WA_LANG,
  WA_COMMUNITY_BM,
  WA_COMMUNITY_BC,
} from "../../../Data/LandingPage/SPM2026Data";
const functions = getFunctions(app, "asia-southeast1");
const submitSpmRegistration = httpsCallable(functions, "submitSpmRegistration");
const EMAIL_STORAGE_KEY = "emailForSpmVerification";
const getWhatsAppCommunityLink = (language: string) =>
  language === "BM/BI" ? WA_COMMUNITY_BM : WA_COMMUNITY_BC;
type AccountEmailType = "password-reset" | "verification";

const isOperationNotAllowedError = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "code" in error &&
  (error as { code?: string }).code === "auth/operation-not-allowed";

const getEmailActionLabel = (emailType: AccountEmailType) =>
  emailType === "password-reset" ? "password reset" : "verification";

export default function RegistrationForm({ id }: { id: string }) {
  const [form, setForm] = useState({
    name: "",
    schoolName: "",
    identity: "",
    country: "Malaysia",
    countryCode: "+60",
    phoneNumber: "",
    email: "",
    discoverySource: "",
    WhatsappCommunityLanguage: "",
  });
  const [subjects, setSubjects] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [accountActivationRequired, setAccountActivationRequired] =
    useState(false);
  const [accountEmailSent, setAccountEmailSent] = useState(true);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [accountEmailType, setAccountEmailType] =
    useState<AccountEmailType>("verification");
  const [accountEmailUsedFallback, setAccountEmailUsedFallback] =
    useState(false);
  const [pendingSubmissionId, setPendingSubmissionId] = useState("");
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isFirstRegistration, setIsFirstRegistration] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionLockRef = useRef(false);
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = window.setTimeout(
      () => setResendCooldown((seconds) => Math.max(0, seconds - 1)),
      1000,
    );
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const sendVerificationEmail = async (email: string, submissionId: string) => {
    const verificationUrl = new URL(
      "/verify-spm-update",
      window.location.origin,
    );
    if (submissionId) {
      verificationUrl.searchParams.set("submissionId", submissionId);
    }
    await sendSignInLinkToEmail(auth, email, {
      url: verificationUrl.toString(),
      handleCodeInApp: true,
    });
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
  };

  const sendPasswordResetAccessEmail = async (email: string) => {
    window.localStorage.removeItem(EMAIL_STORAGE_KEY);
    await sendPasswordResetEmail(auth, email);
  };

  const sendAccountAccessEmail = async (
    email: string,
    submissionId: string,
    emailType: AccountEmailType,
  ) => {
    if (emailType === "password-reset") {
      await sendPasswordResetAccessEmail(email);
      return { emailType, usedFallback: false };
    }

    try {
      await sendVerificationEmail(email, submissionId);
      return { emailType, usedFallback: false };
    } catch (error) {
      if (!isOperationNotAllowedError(error)) {
        throw error;
      }

      console.warn(
        "Firebase email-link sign-in is disabled; sending password reset email instead.",
        error,
      );
      await sendPasswordResetAccessEmail(email);
      return { emailType: "password-reset" as const, usedFallback: true };
    }
  };

  const handleResendAccountEmail = async () => {
    if (!verificationEmail || isResendingEmail || resendCooldown > 0) return;

    setIsResendingEmail(true);
    setResendMessage("");
    try {
      const delivery = await sendAccountAccessEmail(
        verificationEmail,
        pendingSubmissionId,
        accountEmailType,
      );
      setAccountEmailType(delivery.emailType);
      setAccountEmailUsedFallback(delivery.usedFallback);
      setAccountEmailSent(true);
      setResendMessage(
        `A new ${getEmailActionLabel(
          delivery.emailType,
        )} email was sent to ${verificationEmail}.`,
      );
    } catch (emailError) {
      console.error("Failed to resend account access email:", emailError);
      setResendMessage(
        `We still couldn't send the ${
          accountEmailType === "password-reset"
            ? "password reset"
            : "verification"
        } email. Please wait and try again, or contact support.`,
      );
    } finally {
      setIsResendingEmail(false);
      setResendCooldown(30);
    }
  };

  const set = (field: string, val: string) => {
    setForm((f) => ({ ...f, [field]: val }));
    setErrors((e) => ({ ...e, [field]: "" }));
  };

  const toggleSubject = (s: string) => {
    setSubjects((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    setErrors((e) => ({ ...e, subjects: "" }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Please enter your full name";
    if (!form.schoolName.trim()) e.school = "Please enter your school name";
    if (!form.identity) e.identity = "Please select your identity";
    if (!form.WhatsappCommunityLanguage)
      e.WhatsappCommunityLanguage =
        "Please select your WhatsApp community language";
    if (!form.phoneNumber.trim())
      e.phoneNumber = "Please enter your contact number";
    else if (!/^1(?:[02-46-9]\d{7}|1\d{8})$/.test(form.phoneNumber.trim()))
      e.phoneNumber = "Please enter a valid Malaysian phone number";
    if (!form.email.trim()) e.email = "Please enter your email address";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email address";
    if (subjects.length === 0)
      e.subjects = "Please select at least one subject";
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionLockRef.current) return;
    let shouldOpenWhatsApp = false;

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    submissionLockRef.current = true;
    setIsSubmitting(true);
    setErrors((currentErrors) => ({ ...currentErrors, submit: "" }));

    const createdDate = new Date().toISOString();

    try {
      const leadData = {
        name: form.name.trim(),
        schoolName: form.schoolName.trim(),
        identity: form.identity,
        country: "Malaysia",
        countryCode: "+60",
        phoneNumber: form.phoneNumber.trim(),
        email: form.email.trim(),
        discoverySource: form.discoverySource,
        WhatsappCommunityLanguage: form.WhatsappCommunityLanguage,
        SpmTuition: {
          qualification: "SPM",
          subject: subjects,
        },
        status: "New",
        leadSource: "Website",
        campaign: "SPM Ultimate Revision Class 2026",
        sourceDetail: "Landing Page",
        createdDate,
        accountStatus: "not registered",
        portalProvisioningStatus: "pending",
      };

      const referralCode = new URLSearchParams(window.location.search)
        .get("ref")
        ?.trim()
        .toLowerCase();
      const provisionResult = await submitSpmRegistration({
        leadData,
        ...(referralCode ? { referralCode } : {}),
      });
      const provisionData = provisionResult.data as {
        accountActivationRequired?: boolean;
        submissionId?: string;
        isFirstRegistration?: boolean;
        whatsappCommunityLanguageChanged?: boolean;
        emailAction?: "password-reset" | "email-link-verification";
      };
      setAccountActivationRequired(
        provisionData.accountActivationRequired === true,
      );
      const normalizedEmail = leadData.email.trim().toLowerCase();
      const submissionId = provisionData.submissionId || "";
      const isFirstRegistration = provisionData.isFirstRegistration === true;
      const emailType =
        provisionData.emailAction === "password-reset" || isFirstRegistration
          ? "password-reset"
          : "verification";
      setVerificationEmail(normalizedEmail);
      setPendingSubmissionId(submissionId);
      setAccountEmailType(emailType);
      setAccountEmailUsedFallback(false);
      shouldOpenWhatsApp =
        isFirstRegistration ||
        provisionData.whatsappCommunityLanguageChanged === true;
      setIsFirstRegistration(isFirstRegistration);
      try {
        const delivery = await sendAccountAccessEmail(
          normalizedEmail,
          submissionId,
          emailType,
        );
        setAccountEmailType(delivery.emailType);
        setAccountEmailUsedFallback(delivery.usedFallback);
        setAccountEmailSent(true);
      } catch (emailError) {
        setAccountEmailSent(false);
        console.error("Failed to send account access email:", emailError);
      }

      // Track form submit
      if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("event", "form_submit", {
          event_category: "SPM2026",
          event_label: "registration",
        });
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Failed to save SPM2026 lead:", err);
      setErrors((e) => ({
        ...e,
        submit: "Failed to submit, Please try again later",
      }));
      return;
    } finally {
      submissionLockRef.current = false;
      setIsSubmitting(false);
    }

    if (shouldOpenWhatsApp) {
      const whatsappCommunityLink = getWhatsAppCommunityLink(
        form.WhatsappCommunityLanguage,
      );
      setTimeout(() => window.open(whatsappCommunityLink, "_blank"), 800);
    }
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 rounded-xl border text-[14px] text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
      errors[field]
        ? "border-red-400 focus:ring-red-200 bg-red-50"
        : "border-slate-200 bg-white focus:border-emerald-300 focus:ring-emerald-100"
    }`;

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center text-center py-16 px-6"
      >
        <div
          className={`w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4
        ${accountEmailSent ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"}`}
        >
          {accountEmailSent ? (
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          ) : (
            <AlertCircle className="w-8 h-8 text-amber-600" />
          )}
        </div>
        <h3 className="text-[22px] text-slate-900 font-extrabold mb-2">
          {accountEmailSent
            ? "Registration Successful! 🎉"
            : accountEmailType === "password-reset"
              ? "Registration information stored but password reset email failed to send"
              : "Registration information stored but email verification failed to send"}
        </h3>
        <p className="text-[14px] text-slate-500 mb-2 leading-relaxed">
          {!accountEmailSent
            ? accountEmailType === "password-reset"
              ? "Your account has been created. Please check your email to reset your password and access your account."
              : "Your information has been submitted. Please check your email to verify your information"
            : isFirstRegistration
              ? "Thank you for registering! A WhatsApp window is about to open."
              : "Thank you for updating your information! Verify your details in email to confirm your updates."}
        </p>
        <p className="text-[13px] text-slate-600 mb-4 leading-relaxed">
          {!accountEmailSent
            ? `Unable to send ${
                accountEmailType === "password-reset"
                  ? "password reset link"
                  : "verification link"
              } to ${verificationEmail}.Please click the button below to try again, no need to resubmit the form.`
            : accountEmailType === "verification" && accountActivationRequired
              ? "Please check your email and click the secure login link to verify your identity and apply the latest changes."
              : accountEmailUsedFallback
                ? "Passwordless email links are not enabled, so we sent a password reset email instead. Set your password, then log in to apply your latest registration details."
                : "We have sent a password reset email to your email address. Please click the link in the email to set your student portal account password."}
        </p>
        {!accountEmailSent && (
          <button
            type="button"
            onClick={handleResendAccountEmail}
            disabled={isResendingEmail || resendCooldown > 0}
            className="mb-3 flex items-center gap-2 rounded-xl bg-amber-500 px-6 py-3 text-[14px] font-bold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isResendingEmail ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {isResendingEmail
              ? "Sending…"
              : resendCooldown > 0
                ? `Please try again after ${resendCooldown}s`
                : accountEmailType === "password-reset"
                  ? "Send password reset link"
                  : "Send verification link"}
          </button>
        )}
        {resendMessage && (
          <p
            className={`mb-4 text-[13px] leading-relaxed ${
              accountEmailSent ? "text-green-700" : "text-red-700"
            }`}
            role="status"
          >
            {resendMessage}
          </p>
        )}
        <a
          href={getWhatsAppCommunityLink(form.WhatsappCommunityLanguage)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-green-500 text-white text-[14px] hover:bg-green-600 transition-colors font-bold"
        >
          <MessageCircle className="w-4 h-4" /> Open WhatsApp
        </a>
      </motion.div>
    );
  }

  return (
    <form
      id={id}
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 p-6 lg:p-8"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] text-slate-600 uppercase tracking-widest font-bold">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Sarah Tan"
            className={inputClass("name")}
          />
          {errors.name && (
            <p className="text-[11px] text-red-500">{errors.name}</p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] text-slate-600 uppercase tracking-widest font-bold">
            School <span className="text-red-500">*</span>
          </label>
          <input
            value={form.schoolName}
            onChange={(e) => set("schoolName", e.target.value)}
            placeholder="e.g. SMK Taman Desa"
            className={inputClass("schoolName")}
          />
          {errors.schoolName && (
            <p className="text-[11px] text-red-500">{errors.schoolName}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] text-slate-600 uppercase tracking-widest font-bold">
          I Am A <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {[
            "Form 4 Student",
            "Form 5 / SPM 2026 Student",
            "Parent",
            "Teacher",
          ].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => set("identity", opt)}
              className={`px-4 py-2 rounded-xl border text-[13px] transition-all cursor-pointer ${
                form.identity === opt
                  ? "bg-emerald-500 border-emerald-500 text-white font-bold"
                  : "bg-white border-slate-200 text-slate-600 hover:border-emerald-200 font-medium"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        {errors.identity && (
          <p className="text-[11px] text-red-500">{errors.identity}</p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] text-slate-600 uppercase tracking-widest font-bold">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-row gap-1">
            <span
              className={`w-20 px-3 py-3 rounded-xl border text-[14px] text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                errors["phoneNumber"]
                  ? "border-red-400 focus:ring-red-200 bg-red-50"
                  : "border-slate-200 bg-white focus:border-emerald-300 focus:ring-emerald-100"
              }`}
            >
              +60
            </span>
            <input
              value={form.phoneNumber}
              onChange={(e) => set("phoneNumber", e.target.value)}
              placeholder="123456789"
              type="tel"
              className={inputClass("phoneNumber")}
            />
          </div>
          {errors.phoneNumber && (
            <p className="text-[11px] text-red-500">{errors.phoneNumber}</p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] text-slate-600 uppercase tracking-widest font-bold">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="example@email.com"
            type="email"
            className={inputClass("email")}
          />
          {errors.email && (
            <p className="text-[11px] text-red-500">{errors.email}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-[12px] text-slate-600 uppercase tracking-widest font-bold">
          Select Subject(s) <span className="text-red-500">*</span>
          <span className="ml-2 text-[11px] text-slate-400 normal-case font-normal">
            (can choose more than one)
          </span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {SUBJECT_LIST.map((s) => {
            const sub = SUBJECTS.find((x) => x.name === s)!;
            const selected = subjects.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleSubject(s)}
                className={`px-3 py-2.5 rounded-xl border-2 text-[12px] text-left transition-all flex items-center gap-2 cursor-pointer ${
                  selected
                    ? "border-emerald-500 bg-emerald-50 text-emerald-900 font-bold"
                    : "border-slate-200 bg-white text-slate-600 hover:border-emerald-200 font-medium"
                }`}
              >
                {selected ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                ) : (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                )}
                <span className="leading-tight">{s}</span>
              </button>
            );
          })}
        </div>
        {errors.subjects && (
          <p className="text-[11px] text-red-500">{errors.subjects}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] text-slate-600 uppercase tracking-widest font-bold">
          How Did You Hear About Us?{" "}
          <span className="text-slate-400 normal-case font-normal">
            (optional)
          </span>
        </label>
        <div className="flex flex-wrap gap-2">
          {[
            "Facebook",
            "Instagram",
            "Xiaohongshu",
            "Teacher",
            "Parent",
            "Friend",
            "SCS Education Staff",
            "Others",
          ].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => set("discoverySource", opt)}
              className={`px-3.5 py-1.5 rounded-full border text-[12px] transition-all cursor-pointer ${
                form.discoverySource === opt
                  ? "bg-slate-800 border-slate-800 text-white font-bold"
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-400 font-normal"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] text-slate-600 uppercase tracking-widest font-bold">
          Choose Your WhatsApp Community Language{" "}
          <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {WA_LANG.map((opt) => (
            <button
              key={opt.name}
              type="button"
              onClick={() => set("WhatsappCommunityLanguage", opt.name)}
              className={`px-4 py-2 rounded-xl border text-[13px] transition-all cursor-pointer ${
                form.WhatsappCommunityLanguage === opt.name
                  ? "bg-emerald-500 border-emerald-500 text-white font-bold"
                  : "bg-white border-slate-200 text-slate-600 hover:border-emerald-200 font-medium"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {errors.WhatsappCommunityLanguage && (
          <p className="text-[11px] text-red-500">
            {errors.WhatsappCommunityLanguage}
          </p>
        )}
      </div>

      <div className="text-[11px] text-slate-400 leading-relaxed">
        By submitting this form, you agree to SCS Education collecting and using
        your personal information to contact you and provide relevant
        educational information. Your data will be kept confidential.
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-[16px] font-extrabold shadow-xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            🎓 Register for FREE <ArrowRight className="w-5 h-5" />
          </>
        )}
      </button>

      {errors.submit && (
        <p className="text-center text-red-500 text-[12px]">{errors.submit}</p>
      )}

      <p className="text-center text-[12px] text-slate-400">
        Limited availability. Submission will redirect you to WhatsApp for
        confirmation.
      </p>
    </form>
  );
}
